import { Alert, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconChecklist, IconCircleCheck, IconHelpCircle } from '@tabler/icons-react';
import type { QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { runTreatmentPlanQualityChecks } from '../../../lib/treatmentPlanQualityChecks';

export interface TreatmentPlanQualityPanelProps {
  items: QuestionnaireResponseItem[] | undefined;
}

/**
 * Deterministic treatment-plan quality warnings (same presentation family as the
 * clinical-note quality checks). Local and synchronous — no loading state, no AI.
 * These are advisory; signing enforcement happens in save validation.
 */
export function TreatmentPlanQualityPanel({ items }: TreatmentPlanQualityPanelProps): JSX.Element {
  const checks = useMemo(() => runTreatmentPlanQualityChecks(items), [items]);
  const warnings = checks.filter((check) => check.status === 'warning');

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size="sm" variant="light" color="blue" radius="xl">
            <IconChecklist size={12} />
          </ThemeIcon>
          <Title order={5}>Quality checks</Title>
        </Group>

        {warnings.length === 0 ? (
          <Alert color="green" variant="light" icon={<IconCircleCheck size={16} />}>
            <Text size="sm">No quality issues found.</Text>
          </Alert>
        ) : (
          warnings.map((check) => (
            /* Icon/title rendered inline (not via Alert props) so the findings
               below span the full card width instead of indenting under the icon. */
            <Alert key={check.id} color="yellow" variant="light">
              <Group gap={8} wrap="nowrap" mb={6} c="var(--mantine-color-yellow-light-color)">
                <IconHelpCircle size={16} />
                <Text size="sm" fw={600}>{check.label}</Text>
              </Group>
              <Stack gap={6}>
                {check.findings.map((finding, index) => (
                  <Text key={`${finding.location}-${index}`} size="sm" c="gray.7">
                    <Text component="span" fw={600}>{finding.location}:</Text>{' '}
                    {finding.message}
                  </Text>
                ))}
              </Stack>
            </Alert>
          ))
        )}
      </Stack>
    </Paper>
  );
}
